import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ArrowLeft, Eye, Stethoscope, ClipboardList, ChevronDown, ChevronUp,
  Plus, Trash2, Printer, Save, FileText, Pill, Glasses, Contact,
  Calendar, Clock, Phone, History, Search, Check, Scissors, MessageCircle,
  Loader2, CloudOff, Cloud
} from 'lucide-react';

import { AvailableTimeSelect } from '../components/AvailableTimeSelect';
const normalizeText = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Initial state for a new evaluation
const getInitialEvaluation = () => ({
  // Column 1: Historia Clínica
  motivo_consulta: '',
  padecimiento_actual: '',
  antecedentes: {
    personales_patologicos: '',
    oftalmologicos: '',
    quirurgicos: '',
    alergias: '',
    familiares: '',
    habitos: ''
  },
  medicamentos_actuales: [],
  medicamentos_previos: [],
  
  // Column 2: Examen Oftalmológico
  examen: {
    agudeza_visual: {
      od_sin_correccion: '',
      od_con_correccion: '',
      os_sin_correccion: '',
      os_con_correccion: ''
    },
    lensometria: {
      od: { esfera: '', cilindro: '', eje: '', adicion: '' },
      os: { esfera: '', cilindro: '', eje: '', adicion: '' }
    },
    refraccion: {
      od: { esfera: '', cilindro: '', eje: '', adicion: '' },
      os: { esfera: '', cilindro: '', eje: '', adicion: '' }
    },
    presion_intraocular: { od: '', os: '', metodo: 'Goldmann' },
    segmento_anterior: '',
    segmento_posterior: '',
    motilidad_ocular: ''
  },
  
  // Column 3: Diagnóstico y Plan
  diagnostico_ids: [],
  estudios_a_realizar: [],
  plan: '',
  lectura_examenes: '',
  recomendaciones: '',
  proxima_cita: {
    agendar: false,
    fecha: '',
    hora: '',
    tipo: 'control',
    notas: ''
  },
  receta_medicamentos: [],
  receta_medicamentos_observaciones: '',  // Plain text observations for the medication prescription
  recetas_anteojos: [],  // Array of eyeglass prescriptions
  recetas_lentes_contacto: [],  // Array of contact lens prescriptions
  cirugia_recomendada: {
    recomendar: false,
    tipo_cirugia_id: '',
    nivel_interes: 'medio',
    observaciones: ''
  }
});

const EvaluacionKanbanPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedEvalId = searchParams.get('evaluacion');
  const openedRequestedEval = useRef(false);
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [currentEval, setCurrentEval] = useState(getInitialEvaluation());
  const [editingId, setEditingId] = useState(null);
  const [editingFecha, setEditingFecha] = useState(null);
  const [viewingHistoryFecha, setViewingHistoryFecha] = useState(null);
  const currentConsultRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Autosave states
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  
  // Medications autocomplete
  const [medicationsCatalog, setMedicationsCatalog] = useState([]);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [searchingMeds, setSearchingMeds] = useState(false);
  const [openMedPopover, setOpenMedPopover] = useState(null); // Index of open popover
  
  // Availability states
  const [blockedDates, setBlockedDates] = useState([]);
  const [availabilityRules, setAvailabilityRules] = useState([]);
  
  // Collapsible states
  const [openSections, setOpenSections] = useState({
    antecedentes: true,
    medicamentos: true,
    examen: true,
    diagnostico: true,
    recomendaciones: true,
    cirugiaRecomendada: false,
    proximaCita: true,
    recetaMedicamentos: true,
    recetaAnteojos: false,
    recetaLentesContacto: false
  });
  
  // Dialog states
  const [showHistorial, setShowHistorial] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printType, setPrintType] = useState(null);
  const [printRecetaId, setPrintRecetaId] = useState(null);  // For individual prescription printing
  const [showNewDiagnosisDialog, setShowNewDiagnosisDialog] = useState(false);
  const [newDiagnosis, setNewDiagnosis] = useState({ name: '', category: '' });
  const [creatingDiagnosis, setCreatingDiagnosis] = useState(false);
  
  // Diagnosis search/autocomplete
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
  const diagnosisInputRef = useRef(null);
  
  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Fetch availability data
  const fetchAvailability = useCallback(async () => {
    try {
      const [blockedRes, rulesRes] = await Promise.all([
        axios.get(`${API}/availability/blocked-dates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/availability/rules`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBlockedDates(blockedRes.data.blocked_dates || []);
      setAvailabilityRules(rulesRes.data || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  }, [token]);

  // Autosave function
  const performAutoSave = useCallback(async (dataToSave) => {
    // Only autosave if we have motivo_consulta (minimum required field)
    if (!dataToSave.motivo_consulta?.trim()) {
      return;
    }
    
    setAutoSaveStatus('saving');
    try {
      const payload = {
        ...dataToSave,
        patient_id: patientId,
        fecha: editingFecha || format(new Date(), 'yyyy-MM-dd')
      };
      
      if (editingId) {
        await axios.put(`${API}/evaluations/kanban/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API}/evaluations/kanban`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Set the editingId so future saves update instead of create
        if (response.data?.id) {
          // If the user jumped to a past evaluation meanwhile, attach the new id to the saved draft instead
          if (currentConsultRef.current && !currentConsultRef.current.editingId) {
            currentConsultRef.current.editingId = response.data.id;
          } else {
            setEditingId(response.data.id);
          }
        }
      }
      
      // Also save persistent medical data to patient
      await axios.put(`${API}/patients/${patientId}/medical-data`, {
        antecedentes: dataToSave.antecedentes,
        medicamentos_actuales: dataToSave.medicamentos_actuales
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      lastSavedDataRef.current = JSON.stringify(dataToSave);
      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 3000);
      
    } catch (error) {
      console.error('Autosave error:', error);
      setAutoSaveStatus('error');
      // Reset to pending after 5 seconds to retry
      setTimeout(() => {
        setAutoSaveStatus('pending');
      }, 5000);
    }
  }, [editingId, editingFecha, patientId, token]);

  // Watch for changes and trigger autosave with debounce
  useEffect(() => {
    // Skip if loading or no data entered
    if (loading) return;
    
    const currentData = JSON.stringify(currentEval);
    const lastSaved = lastSavedDataRef.current;
    
    // Check if data has actually changed
    if (currentData === lastSaved) {
      return;
    }
    
    // Check if there's meaningful data to save
    const hasData = currentEval.motivo_consulta?.trim() || 
                    currentEval.padecimiento_actual?.trim() ||
                    currentEval.diagnostico_ids?.length > 0 ||
                    currentEval.receta_medicamentos?.length > 0;
    
    if (!hasData) {
      return;
    }
    
    setHasUnsavedChanges(true);
    setAutoSaveStatus('pending');
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for autosave (3 seconds after last change)
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave(currentEval);
    }, 3000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [currentEval, loading, performAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Check if a date is available
  const isDateAvailable = (dateStr) => {
    if (!dateStr) return true;
    const date = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = date.getDay(); // 0 = Sunday
    
    // Check if it's Sunday (closed by default)
    if (dayOfWeek === 0) return false;
    
    // Check if it's blocked
    if (blockedDates.includes(dateStr)) return false;
    
    // Check availability rules - weekday is a number (0=Monday, 6=Sunday in backend)
    // Convert JS day (0=Sunday) to backend day (0=Monday)
    const backendWeekday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const hasRule = availabilityRules.some(rule => rule.weekday === backendWeekday && rule.is_active);
    
    return hasRule || availabilityRules.length === 0;
  };

  // Fetch medications catalog
  const fetchMedications = useCallback(async (search = '') => {
    try {
      setSearchingMeds(true);
      const res = await axios.get(`${API}/medications/search?q=${search}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedicationsCatalog(res.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    } finally {
      setSearchingMeds(false);
    }
  }, [token]);

  // Create new medication
  const createMedication = async (nombre) => {
    try {
      const res = await axios.post(`${API}/medications`, { nombre }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Medicamento "${nombre}" agregado al catálogo`);
      return res.data;
    } catch (error) {
      console.error('Error creating medication:', error);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [patientRes, evalRes, diagRes, surgeryTypesRes] = await Promise.all([
        axios.get(`${API}/patients/${patientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evaluations/kanban/patient/${patientId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evaluations/diagnoses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/surgery-crm/surgery-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const patientData = patientRes.data;
      setPatient(patientData);
      setEvaluations(evalRes.data);
      setDiagnoses(diagRes.data);
      setSurgeryTypes(surgeryTypesRes.data || []);

      // On the first load, reuse TODAY's evaluation if it already exists so we don't
      // create a brand-new record every time the chart is opened (an evaluation can last all day).
      if (!initialLoadDoneRef.current && !editingId) {
        initialLoadDoneRef.current = true;
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        const todaysEval = (evalRes.data || []).find(e => e.fecha === todayStr);
        if (todaysEval) {
          const loaded = buildEvalState(todaysEval, patientData);
          lastSavedDataRef.current = JSON.stringify(loaded);
          setCurrentEval(loaded);
          setEditingId(todaysEval.id);
          setEditingFecha(todaysEval.fecha);
          setAutoSaveStatus('idle');
          toast.info('Continuando la evaluación de hoy');
        } else {
          // Fresh evaluation: seed with patient's persistent medical data
          setCurrentEval(prev => ({
            ...prev,
            antecedentes: patientData.antecedentes || getInitialEvaluation().antecedentes,
            medicamentos_actuales: patientData.medicamentos_actuales || []
          }));
        }
      } else if (!editingId) {
        // Subsequent reloads (e.g. after save) with a fresh form: keep persistent medical data in sync
        setCurrentEval(prev => ({
          ...prev,
          antecedentes: patientData.antecedentes || getInitialEvaluation().antecedentes,
          medicamentos_actuales: patientData.medicamentos_actuales || []
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [patientId, token, editingId]);

  useEffect(() => {
    fetchData();
    fetchAvailability();
  }, [fetchData, fetchAvailability]);

  const handleSave = async () => {
    if (!currentEval.motivo_consulta?.trim()) {
      toast.error('El motivo de consulta es requerido');
      return;
    }
    
    // Clear autosave timer to prevent conflicts
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    setSaving(true);
    setAutoSaveStatus('saving');
    try {
      const payload = {
        ...currentEval,
        patient_id: patientId,
        fecha: editingFecha || format(new Date(), 'yyyy-MM-dd')
      };
      
      let createdId = null;
      if (editingId) {
        await axios.put(`${API}/evaluations/kanban/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evaluación actualizada');
      } else {
        const resp = await axios.post(`${API}/evaluations/kanban`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        createdId = resp.data?.id;
        toast.success('Evaluación guardada');
      }
      
      // Also save persistent medical data to patient
      await axios.put(`${API}/patients/${patientId}/medical-data`, {
        antecedentes: currentEval.antecedentes,
        medicamentos_actuales: currentEval.medicamentos_actuales
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update autosave reference
      lastSavedDataRef.current = JSON.stringify(currentEval);
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

      // Keep TODAY's evaluation open so it can keep being edited all day (no duplicate records)
      if (createdId) {
        setEditingId(createdId);
        if (!editingFecha) setEditingFecha(format(new Date(), 'yyyy-MM-dd'));
      }

      // Refresh the history list without wiping the current form
      try {
        const evalRes = await axios.get(`${API}/evaluations/kanban/patient/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEvaluations(evalRes.data);
      } catch (e) { /* non-blocking */ }
      
      // Reset autosave status after a delay
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error('Error al guardar la evaluación');
      setAutoSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // Build the editable state object from a stored evaluation (reused on manual load and auto-load of today's evaluation)
  const buildEvalState = (evaluation, patientData) => {
    const p = patientData || patient;
    return {
      motivo_consulta: evaluation.motivo_consulta || '',
      padecimiento_actual: evaluation.padecimiento_actual || '',
      antecedentes: p?.antecedentes || evaluation.antecedentes || getInitialEvaluation().antecedentes,
      medicamentos_actuales: p?.medicamentos_actuales || evaluation.medicamentos_actuales || [],
      medicamentos_previos: evaluation.medicamentos_previos || [],
      examen: (() => {
        const def = getInitialEvaluation().examen;
        const ex = evaluation.examen || {};
        return {
          ...def,
          ...ex,
          agudeza_visual: { ...def.agudeza_visual, ...(ex.agudeza_visual || {}) },
          lensometria: {
            od: { ...def.lensometria.od, ...(ex.lensometria?.od || {}) },
            os: { ...def.lensometria.os, ...(ex.lensometria?.os || {}) }
          },
          refraccion: {
            od: { ...def.refraccion.od, ...(ex.refraccion?.od || {}) },
            os: { ...def.refraccion.os, ...(ex.refraccion?.os || {}) }
          },
          presion_intraocular: { ...def.presion_intraocular, ...(ex.presion_intraocular || {}) }
        };
      })(),
      diagnostico_ids: evaluation.diagnostico_ids || [],
      estudios_a_realizar: evaluation.estudios_a_realizar || [],
      plan: evaluation.plan || '',
      lectura_examenes: evaluation.lectura_examenes || '',
      recomendaciones: evaluation.recomendaciones || '',
      proxima_cita: evaluation.proxima_cita || getInitialEvaluation().proxima_cita,
      receta_medicamentos: evaluation.receta_medicamentos || [],
      receta_medicamentos_observaciones: evaluation.receta_medicamentos_observaciones || '',
      recetas_anteojos: evaluation.recetas_anteojos ||
        (evaluation.receta_anteojos ? [{ ...evaluation.receta_anteojos, id: crypto.randomUUID() }] : []),
      recetas_lentes_contacto: evaluation.recetas_lentes_contacto ||
        (evaluation.receta_lentes_contacto ? [{ ...evaluation.receta_lentes_contacto, id: crypto.randomUUID() }] : []),
      cirugia_recomendada: evaluation.cirugia_recomendada || getInitialEvaluation().cirugia_recomendada
    };
  };

  const handleLoadEvaluation = (evaluation) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    // Remember the consultation in progress so the user can come back to it
    if (!viewingHistoryFecha) {
      currentConsultRef.current = { evaluation: currentEval, editingId, editingFecha };
    }
    // When loading an evaluation, use PATIENT's current antecedentes and medicamentos_actuales
    // (not the historical snapshot from the evaluation)
    const loaded = buildEvalState(evaluation);
    lastSavedDataRef.current = JSON.stringify(loaded);
    setCurrentEval(loaded);
    setEditingId(evaluation.id);
    setEditingFecha(evaluation.fecha);
    setViewingHistoryFecha(evaluation.fecha);
    setAutoSaveStatus('idle');
    setShowHistorial(false);
  };

  const handleBackToCurrentConsult = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const saved = currentConsultRef.current;
    if (saved) {
      setCurrentEval(saved.evaluation);
      setEditingId(saved.editingId);
      setEditingFecha(saved.editingFecha);
    } else {
      setCurrentEval({
        ...getInitialEvaluation(),
        antecedentes: patient?.antecedentes || getInitialEvaluation().antecedentes,
        medicamentos_actuales: patient?.medicamentos_actuales || []
      });
      setEditingId(null);
      setEditingFecha(null);
    }
    currentConsultRef.current = null;
    setViewingHistoryFecha(null);
    toast.info('Volvió a la consulta actual');
  };

  useEffect(() => {
    if (!requestedEvalId || openedRequestedEval.current || !patient) return;
    const target = evaluations.find(e => e.id === requestedEvalId);
    if (target) {
      openedRequestedEval.current = true;
      handleLoadEvaluation(target);
      toast.info(`Evaluación del ${target.fecha.split('-').reverse().join('/')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedEvalId, evaluations, patient]);


  const handleDeleteEvaluation = async (evalId) => {
    if (!window.confirm('¿Está seguro de eliminar esta evaluación?')) return;
    
    try {
      await axios.delete(`${API}/evaluations/kanban/${evalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evaluación eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const addMedicamento = (type) => {
    const newMed = { id: crypto.randomUUID(), nombre: '', dosis: '', frecuencia: '' };
    if (type === 'actual') {
      setCurrentEval(prev => ({
        ...prev,
        medicamentos_actuales: [...prev.medicamentos_actuales, newMed]
      }));
    } else if (type === 'previo') {
      setCurrentEval(prev => ({
        ...prev,
        medicamentos_previos: [...prev.medicamentos_previos, newMed]
      }));
    } else if (type === 'receta') {
      setCurrentEval(prev => ({
        ...prev,
        receta_medicamentos: [...prev.receta_medicamentos, { 
          id: crypto.randomUUID(),
          nombre: '', componente_activo: '', dosis: '', frecuencia: '', 
          duracion: '', via: '', instrucciones: '' 
        }]
      }));
    }
  };

  const removeMedicamento = (type, index) => {
    if (type === 'actual') {
      setCurrentEval(prev => ({
        ...prev,
        medicamentos_actuales: prev.medicamentos_actuales.filter((_, i) => i !== index)
      }));
    } else if (type === 'previo') {
      setCurrentEval(prev => ({
        ...prev,
        medicamentos_previos: prev.medicamentos_previos.filter((_, i) => i !== index)
      }));
    } else if (type === 'receta') {
      setCurrentEval(prev => ({
        ...prev,
        receta_medicamentos: prev.receta_medicamentos.filter((_, i) => i !== index)
      }));
    }
  };

  const addDiagnosis = (diagnosisId) => {
    if (!currentEval.diagnostico_ids.includes(diagnosisId)) {
      setCurrentEval(prev => ({
        ...prev,
        diagnostico_ids: [...prev.diagnostico_ids, diagnosisId]
      }));
    }
    // Clear search after adding
    setDiagnosisSearch('');
    setShowDiagnosisDropdown(false);
  };

  const removeDiagnosis = (diagnosisId) => {
    setCurrentEval(prev => ({
      ...prev,
      diagnostico_ids: prev.diagnostico_ids.filter(id => id !== diagnosisId)
    }));
  };

  // Filter diagnoses based on search and exclude already selected (memoized: catalog can have thousands)
  const normalizedDiagnoses = useMemo(
    () => diagnoses.map(d => ({ ...d, _norm: normalizeText(d.name) })),
    [diagnoses]
  );
  const diagnosisQuery = normalizeText(diagnosisSearch);
  const selectedDiagnosisIds = currentEval.diagnostico_ids;
  const { filteredDiagnoses, exactDiagnosis } = useMemo(() => {
    if (!diagnosisQuery) return { filteredDiagnoses: [], exactDiagnosis: null };
    const rank = (n) => {
      if (n === diagnosisQuery) return 0;
      if (n.startsWith(diagnosisQuery)) return 1;
      if (n.split(/[\s,]+/).some(w => w.startsWith(diagnosisQuery))) return 2;
      return 3;
    };
    const seen = new Set();
    const list = normalizedDiagnoses
      .filter(d => !selectedDiagnosisIds.includes(d.id) && d._norm.includes(diagnosisQuery))
      .map(d => ({ d, r: rank(d._norm) }))
      .sort((x, y) => x.r - y.r || x.d.name.length - y.d.name.length || x.d.name.localeCompare(y.d.name))
      .map(x => x.d)
      .filter(d => (seen.has(d._norm) ? false : seen.add(d._norm)));
    return {
      filteredDiagnoses: list,
      exactDiagnosis: normalizedDiagnoses.find(d => d._norm === diagnosisQuery) || null,
    };
  }, [normalizedDiagnoses, diagnosisQuery, selectedDiagnosisIds]);
  const exactDiagnosisMatch = Boolean(exactDiagnosis);

  // Quick create diagnosis from search
  const handleQuickCreateDiagnosis = async () => {
    if (!diagnosisSearch.trim()) return;
    
    setCreatingDiagnosis(true);
    try {
      const response = await axios.post(`${API}/evaluations/diagnoses`, {
        name: diagnosisSearch.trim(),
        category: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdDiagnosis = response.data;
      setDiagnoses(prev => [...prev, createdDiagnosis].sort((a, b) => a.name.localeCompare(b.name)));
      addDiagnosis(createdDiagnosis.id);
      toast.success(`Diagnóstico "${createdDiagnosis.name}" creado`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Ya existe un diagnóstico con ese nombre');
      } else {
        toast.error('Error al crear el diagnóstico');
      }
    } finally {
      setCreatingDiagnosis(false);
    }
  };

  // Functions for multiple eyeglass prescriptions
  const addRecetaAnteojos = () => {
    const newReceta = {
      id: crypto.randomUUID(),
      nombre: `Receta ${currentEval.recetas_anteojos.length + 1}`,
      od_esfera: '', od_cilindro: '', od_eje: '', od_adicion: '', od_dp: '',
      os_esfera: '', os_cilindro: '', os_eje: '', os_adicion: '', os_dp: '',
      tipo_lente: '', material: '', tratamientos: '', observaciones: ''
    };
    setCurrentEval(prev => ({
      ...prev,
      recetas_anteojos: [...prev.recetas_anteojos, newReceta]
    }));
  };

  const removeRecetaAnteojos = (id) => {
    setCurrentEval(prev => ({
      ...prev,
      recetas_anteojos: prev.recetas_anteojos.filter(r => r.id !== id)
    }));
  };

  const updateRecetaAnteojos = (id, field, value) => {
    setCurrentEval(prev => ({
      ...prev,
      recetas_anteojos: prev.recetas_anteojos.map(r => 
        r.id === id ? { ...r, [field]: value } : r
      )
    }));
  };

  // Copy the Refracción values from the exam into the FIRST eyeglass prescription.
  // Creates the first prescription if none exists.
  const copyRefraccionToReceta = () => {
    const ref = currentEval.examen?.refraccion || { od: {}, os: {} };
    const refFields = {
      od_esfera: ref.od?.esfera || '',
      od_cilindro: ref.od?.cilindro || '',
      od_eje: ref.od?.eje || '',
      od_adicion: ref.od?.adicion || '',
      os_esfera: ref.os?.esfera || '',
      os_cilindro: ref.os?.cilindro || '',
      os_eje: ref.os?.eje || '',
      os_adicion: ref.os?.adicion || ''
    };
    setCurrentEval(prev => {
      let recetas = prev.recetas_anteojos;
      if (recetas.length === 0) {
        recetas = [{
          id: crypto.randomUUID(),
          nombre: 'Receta 1',
          od_esfera: '', od_cilindro: '', od_eje: '', od_adicion: '', od_dp: '',
          os_esfera: '', os_cilindro: '', os_eje: '', os_adicion: '', os_dp: '',
          tipo_lente: '', material: '', tratamientos: '', observaciones: ''
        }];
      }
      const updated = recetas.map((r, idx) => idx === 0 ? { ...r, ...refFields } : r);
      return { ...prev, recetas_anteojos: updated };
    });
    // Make sure the prescriptions section is open so the user sees the result
    setOpenSections(prev => ({ ...prev, recetaAnteojos: true }));
    toast.success('Refracción copiada a la primera receta de anteojos');
  };

  // Functions for multiple contact lens prescriptions
  const addRecetaLentesContacto = () => {
    const newReceta = {
      id: crypto.randomUUID(),
      nombre: `Receta ${currentEval.recetas_lentes_contacto.length + 1}`,
      od_esfera: '', od_cilindro: '', od_eje: '', od_curva_base: '', od_diametro: '', od_marca: '',
      os_esfera: '', os_cilindro: '', os_eje: '', os_curva_base: '', os_diametro: '', os_marca: '',
      tipo: 'blandos', uso: '', observaciones: ''
    };
    setCurrentEval(prev => ({
      ...prev,
      recetas_lentes_contacto: [...prev.recetas_lentes_contacto, newReceta]
    }));
  };

  const removeRecetaLentesContacto = (id) => {
    setCurrentEval(prev => ({
      ...prev,
      recetas_lentes_contacto: prev.recetas_lentes_contacto.filter(r => r.id !== id)
    }));
  };

  const updateRecetaLentesContacto = (id, field, value) => {
    setCurrentEval(prev => ({
      ...prev,
      recetas_lentes_contacto: prev.recetas_lentes_contacto.map(r => 
        r.id === id ? { ...r, [field]: value } : r
      )
    }));
  };

  const handleCreateDiagnosis = async () => {
    if (!newDiagnosis.name.trim()) {
      toast.error('El nombre del diagnóstico es requerido');
      return;
    }
    
    setCreatingDiagnosis(true);
    try {
      const response = await axios.post(`${API}/evaluations/diagnoses`, {
        name: newDiagnosis.name.trim(),
        category: newDiagnosis.category.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add new diagnosis to the list and select it
      const createdDiagnosis = response.data;
      setDiagnoses(prev => [...prev, createdDiagnosis].sort((a, b) => a.name.localeCompare(b.name)));
      addDiagnosis(createdDiagnosis.id);
      
      toast.success('Diagnóstico creado y agregado');
      setShowNewDiagnosisDialog(false);
      setNewDiagnosis({ name: '', category: '' });
    } catch (error) {
      console.error('Error creating diagnosis:', error);
      if (error.response?.status === 400) {
        toast.error('Ya existe un diagnóstico con ese nombre');
      } else {
        toast.error('Error al crear el diagnóstico');
      }
    } finally {
      setCreatingDiagnosis(false);
    }
  };

  const addEstudio = () => {
    setCurrentEval(prev => ({
      ...prev,
      estudios_a_realizar: [...prev.estudios_a_realizar, { id: crypto.randomUUID(), nombre: '', descripcion: '' }]
    }));
  };

  const removeEstudio = (index) => {
    setCurrentEval(prev => ({
      ...prev,
      estudios_a_realizar: prev.estudios_a_realizar.filter((_, i) => i !== index)
    }));
  };

  const handlePrint = (type, recetaId = null) => {
    setPrintType(type);
    setPrintRecetaId(recetaId);
    setShowPrintDialog(true);
  };

  // Function to send prescription via WhatsApp
  const sendViaWhatsApp = (type, recetaId = null) => {
    let message = `*Receta Médica*\n`;
    message += `Paciente: ${patient?.full_name || 'N/A'}\n`;
    message += `Fecha: ${new Date().toLocaleDateString('es-GT')}\n\n`;

    if (type === 'medicamentos') {
      message += `*MEDICAMENTOS:*\n`;
      currentEval.receta_medicamentos.forEach((med, idx) => {
        message += `${idx + 1}. ${med.nombre}\n`;
        if (med.dosis) message += `   Dosis: ${med.dosis}\n`;
        if (med.frecuencia) message += `   Frecuencia: ${med.frecuencia}\n`;
        if (med.instrucciones) message += `   Instrucciones: ${med.instrucciones}\n`;
      });
    } else if (type === 'anteojos' && recetaId) {
      const receta = currentEval.recetas_anteojos.find(r => r.id === recetaId);
      if (receta) {
        message += `*RECETA DE ANTEOJOS${receta.nombre ? ` - ${receta.nombre}` : ''}:*\n\n`;
        message += `OD: Esf ${receta.od_esfera || '-'} Cil ${receta.od_cilindro || '-'} Eje ${receta.od_eje || '-'} Add ${receta.od_adicion || '-'}\n`;
        message += `OS: Esf ${receta.os_esfera || '-'} Cil ${receta.os_cilindro || '-'} Eje ${receta.os_eje || '-'} Add ${receta.os_adicion || '-'}\n`;
        if (receta.tipo_lente) message += `Tipo: ${receta.tipo_lente}\n`;
        if (receta.tratamientos) message += `Tratamientos: ${receta.tratamientos}\n`;
      }
    } else if (type === 'lentes_contacto' && recetaId) {
      const receta = currentEval.recetas_lentes_contacto.find(r => r.id === recetaId);
      if (receta) {
        message += `*RECETA DE LENTES DE CONTACTO${receta.nombre ? ` - ${receta.nombre}` : ''}:*\n\n`;
        message += `OD: Esf ${receta.od_esfera || '-'} Cil ${receta.od_cilindro || '-'} CB ${receta.od_curva_base || '-'} DIA ${receta.od_diametro || '-'}\n`;
        message += `OS: Esf ${receta.os_esfera || '-'} Cil ${receta.os_cilindro || '-'} CB ${receta.os_curva_base || '-'} DIA ${receta.os_diametro || '-'}\n`;
        if (receta.tipo) message += `Tipo: ${receta.tipo}\n`;
        if (receta.uso) message += `Uso: ${receta.uso}\n`;
      }
    }

    const encodedMessage = encodeURIComponent(message);
    const phone = patient?.phone?.replace(/\D/g, '') || '';
    const whatsappUrl = phone 
      ? `https://wa.me/${phone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Function to print in a new window (more reliable)
  const executePrint = () => {
    const printContent = document.getElementById('print-content');
    if (!printContent) {
      toast.error('No se encontró el contenido para imprimir');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=600');
    if (!printWindow) {
      toast.error('No se pudo abrir la ventana de impresión. Verifica que no esté bloqueada.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receta - ${patient?.full_name || 'Paciente'}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding-top: 1.5cm;
            padding-left: 16px;
            padding-right: 16px;
            padding-bottom: 16px;
            background: white;
            color: #1e293b;
            font-size: 12px;
          }
          @page {
            size: 8.5in 5.5in landscape;
            margin: 0;
          }
          @media print {
            body {
              padding-top: 1.5cm;
              padding-left: 0.5cm;
              padding-right: 0.5cm;
              padding-bottom: 0.5cm;
            }
          }
          .container {
            max-width: 8in;
            margin: 0 auto;
          }
          
          /* Patient Info Grid */
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px 16px;
          }
          .flex {
            display: flex;
            align-items: center;
          }
          .gap-2 {
            gap: 8px;
          }
          .text-slate-500 {
            color: #64748b;
          }
          .text-slate-800 {
            color: #1e293b;
          }
          .font-semibold {
            font-weight: 600;
          }
          .font-bold {
            font-weight: 700;
          }
          .text-sm {
            font-size: 14px;
          }
          .text-xs {
            font-size: 12px;
          }
          .border-b {
            border-bottom: 1px solid #e2e8f0;
          }
          .border-slate-200 {
            border-color: #e2e8f0;
          }
          .border-slate-300 {
            border-color: #cbd5e1;
          }
          .pb-4 {
            padding-bottom: 16px;
          }
          .mb-4 {
            margin-bottom: 16px;
          }
          .mt-1 {
            margin-top: 4px;
          }
          .pl-3 {
            padding-left: 12px;
          }
          .border-l-2 {
            border-left: 2px solid #cbd5e1;
          }
          .italic {
            font-style: italic;
          }
          .text-slate-400 {
            color: #94a3b8;
          }
          .text-slate-600 {
            color: #475569;
          }
          .text-slate-700 {
            color: #334155;
          }
          .uppercase {
            text-transform: uppercase;
          }
          .tracking-wide {
            letter-spacing: 0.025em;
          }
          .space-y-4 > * + * {
            margin-top: 16px;
          }
          
          /* Table styles */
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 6px 8px;
            text-align: center;
          }
          th {
            background: #f1f5f9;
            font-weight: 600;
            color: #334155;
          }
          
          /* Medication styles */
          .med-number {
            font-weight: 700;
            color: #475569;
            font-size: 14px;
          }
          .med-name {
            font-weight: 700;
            color: #1e293b;
            font-size: 14px;
          }
          .med-details {
            font-size: 11px;
            color: #475569;
            margin-top: 4px;
          }
          .med-details span {
            margin-right: 12px;
          }
          .med-instructions {
            font-size: 11px;
            color: #334155;
            margin-top: 6px;
            font-style: italic;
            display: flex;
            align-items: flex-start;
            gap: 6px;
          }
          .med-instructions .arrow {
            color: #94a3b8;
            font-weight: 700;
          }
          .warning {
            font-size: 11px;
            color: #d97706;
            font-style: italic;
            margin-top: 12px;
            padding-top: 8px;
            border-top: 1px solid #fde68a;
          }
        </style>
      </head>
      <body>
        <div class="container">
          ${printContent.innerHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 250);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // handleWhatsApp is used only for medicamentos now
  // anteojos and lentes_contacto use sendViaWhatsApp with specific recetaId
  const handleWhatsApp = (type) => {
    if (!patient?.phone) {
      toast.error('El paciente no tiene teléfono registrado');
      return;
    }

    let message = `*Dr. Cristian R. Acevedo*\n_Médico Oftalmólogo_\n\n`;
    message += `📋 *Paciente:* ${patient.full_name}\n`;
    message += `📅 *Fecha:* ${format(new Date(), "d 'de' MMMM, yyyy", { locale: es })}\n\n`;

    if (type === 'medicamentos' && currentEval.receta_medicamentos.length > 0) {
      message += `💊 *RECETA MÉDICA*\n`;
      message += `${'─'.repeat(20)}\n\n`;
      currentEval.receta_medicamentos.forEach((med, idx) => {
        message += `*${idx + 1}. ${med.nombre}*\n`;
        if (med.instrucciones) {
          message += `${med.instrucciones}\n`;
        }
        message += `\n`;
      });
    }

    if (type === 'medicamentos' && currentEval.receta_medicamentos_observaciones) {
      message += `📝 *Observaciones:*\n${currentEval.receta_medicamentos_observaciones}\n`;
    }
    // Add next appointment if scheduled
    if (currentEval.proxima_cita?.agendar && currentEval.proxima_cita?.fecha && currentEval.proxima_cita?.hora) {
      message += `\n📆 *PRÓXIMA CITA*\n`;
      message += `${format(new Date(currentEval.proxima_cita.fecha + 'T12:00:00'), "EEEE d 'de' MMMM, yyyy", { locale: es })}\n`;
      message += `🕐 *Hora:* ${currentEval.proxima_cita.hora}\n`;
    }

    message += `\n_Gracias por su confianza_ 🙏`;

    // Clean phone number and open WhatsApp
    const cleanPhone = patient.phone.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20" data-testid="evaluacion-kanban-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(`/admin/patients/${patientId}`)} className="p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl lg:text-2xl font-bold text-[#0B3B71]" data-testid="evaluation-patient-name">{patient?.full_name}</h1>
              {viewingHistoryFecha && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="viewing-history-banner">
                  <History className="h-3.5 w-3.5" />
                  <span>Viendo evaluación del {viewingHistoryFecha.split('-').reverse().join('/')}</span>
                  <Button
                    size="sm"
                    onClick={handleBackToCurrentConsult}
                    className="h-6 px-2 text-xs bg-[#0B3B71] hover:bg-[#0B3B71]/90 text-white"
                    data-testid="back-to-current-consult-btn"
                  >
                    <ArrowLeft className="h-3 w-3 mr-1" />
                    Volver a la consulta actual
                  </Button>
                </div>
              )}
              {/* Autosave Indicator */}
              {autoSaveStatus !== 'idle' && (
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                  autoSaveStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                  autoSaveStatus === 'saving' ? 'bg-blue-100 text-blue-700' :
                  autoSaveStatus === 'saved' ? 'bg-green-100 text-green-700' :
                  autoSaveStatus === 'error' ? 'bg-red-100 text-red-700' : ''
                }`}>
                  {autoSaveStatus === 'pending' && (
                    <>
                      <CloudOff className="h-3.5 w-3.5" />
                      <span>Cambios sin guardar</span>
                    </>
                  )}
                  {autoSaveStatus === 'saving' && (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Guardando...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <Cloud className="h-3.5 w-3.5" />
                      <span>Guardado</span>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <CloudOff className="h-3.5 w-3.5" />
                      <span>Error al guardar</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {patient?.birth_date && (
                <Badge variant="outline">
                  {calculateAge(patient.birth_date)} años
                </Badge>
              )}
              {patient?.phone && (
                <>
                  <Phone className="h-4 w-4 ml-2" />
                  <span>{patient.phone}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowHistorial(true)}>
            <History className="h-4 w-4 mr-2" />
            Historial ({evaluations.length})
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#0891B2] hover:bg-[#0891B2]/90">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* Column 1: Historia Clínica */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[#0B3B71] font-semibold border-b pb-2">
            <Stethoscope className="h-5 w-5" />
            <span>Historia Clínica</span>
          </div>
          
          {/* Motivo de Consulta */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="h-4 w-4 text-blue-500" />
                Motivo de Consulta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Motivo principal de la consulta..."
                value={currentEval.motivo_consulta}
                onChange={(e) => setCurrentEval(prev => ({ ...prev, motivo_consulta: e.target.value }))}
                className="min-h-[80px] resize-none"
              />
              <div className="mt-3">
                <Label className="text-xs text-slate-500">Padecimiento Actual</Label>
                <Textarea
                  placeholder="Historia detallada del padecimiento..."
                  value={currentEval.padecimiento_actual}
                  onChange={(e) => setCurrentEval(prev => ({ ...prev, padecimiento_actual: e.target.value }))}
                  className="min-h-[80px] resize-none mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Antecedentes */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.antecedentes} onOpenChange={() => toggleSection('antecedentes')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      Antecedentes de Importancia
                    </div>
                    {openSections.antecedentes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                    <span>💾</span> Los antecedentes se guardan permanentemente en el paciente
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Personales Patológicos</Label>
                    <Textarea
                      placeholder="Diabetes, hipertensión, cardiopatía, tiroides..."
                      value={currentEval.antecedentes.personales_patologicos}
                      onChange={(e) => setCurrentEval(prev => ({
                        ...prev,
                        antecedentes: { ...prev.antecedentes, personales_patologicos: e.target.value }
                      }))}
                      className="min-h-[60px] resize-none mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Oftalmológicos</Label>
                    <Textarea
                      placeholder="Cirugías oculares, uso de lentes, glaucoma..."
                      value={currentEval.antecedentes.oftalmologicos}
                      onChange={(e) => setCurrentEval(prev => ({
                        ...prev,
                        antecedentes: { ...prev.antecedentes, oftalmologicos: e.target.value }
                      }))}
                      className="min-h-[60px] resize-none mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Quirúrgicos</Label>
                      <Textarea
                        placeholder="Cirugías previas..."
                        value={currentEval.antecedentes.quirurgicos}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          antecedentes: { ...prev.antecedentes, quirurgicos: e.target.value }
                        }))}
                        className="min-h-[50px] resize-none mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Alergias</Label>
                      <Textarea
                        placeholder="Alergias conocidas..."
                        value={currentEval.antecedentes.alergias}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          antecedentes: { ...prev.antecedentes, alergias: e.target.value }
                        }))}
                        className="min-h-[50px] resize-none mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Familiares</Label>
                      <Textarea
                        placeholder="Antecedentes familiares..."
                        value={currentEval.antecedentes.familiares}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          antecedentes: { ...prev.antecedentes, familiares: e.target.value }
                        }))}
                        className="min-h-[50px] resize-none mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Hábitos</Label>
                      <Textarea
                        placeholder="Tabaco, alcohol, etc..."
                        value={currentEval.antecedentes.habitos}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          antecedentes: { ...prev.antecedentes, habitos: e.target.value }
                        }))}
                        className="min-h-[50px] resize-none mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Medicamentos */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.medicamentos} onOpenChange={() => toggleSection('medicamentos')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4 text-blue-500" />
                      Medicamentos
                      <Badge variant="secondary" className="ml-1">
                        {currentEval.medicamentos_actuales.length + currentEval.medicamentos_previos.length}
                      </Badge>
                    </div>
                    {openSections.medicamentos ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                    <span>💾</span> Los medicamentos actuales se guardan permanentemente en el paciente
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-slate-500">Actuales</Label>
                      <Button variant="ghost" size="sm" onClick={() => addMedicamento('actual')} className="text-blue-600 h-7">
                        <Plus className="h-3 w-3 mr-1" /> Agregar
                      </Button>
                    </div>
                    {currentEval.medicamentos_actuales.map((med, idx) => (
                      <div key={med.id || idx} className="flex gap-2 mb-2 items-center">
                        <Input
                          placeholder="Medicamento"
                          value={med.nombre}
                          onChange={(e) => {
                            const updated = [...currentEval.medicamentos_actuales];
                            updated[idx].nombre = e.target.value;
                            setCurrentEval(prev => ({ ...prev, medicamentos_actuales: updated }));
                          }}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Dosis"
                          value={med.dosis || ''}
                          onChange={(e) => {
                            const updated = [...currentEval.medicamentos_actuales];
                            updated[idx].dosis = e.target.value;
                            setCurrentEval(prev => ({ ...prev, medicamentos_actuales: updated }));
                          }}
                          className="w-24"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeMedicamento('actual', idx)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-slate-500">Previos</Label>
                      <Button variant="ghost" size="sm" onClick={() => addMedicamento('previo')} className="text-blue-600 h-7">
                        <Plus className="h-3 w-3 mr-1" /> Agregar
                      </Button>
                    </div>
                    {currentEval.medicamentos_previos.map((med, idx) => (
                      <div key={med.id || idx} className="flex gap-2 mb-2">
                        <Input
                          placeholder="Medicamento"
                          value={med.nombre}
                          onChange={(e) => {
                            const updated = [...currentEval.medicamentos_previos];
                            updated[idx].nombre = e.target.value;
                            setCurrentEval(prev => ({ ...prev, medicamentos_previos: updated }));
                          }}
                          className="flex-1"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeMedicamento('previo', idx)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>

        {/* Column 2: Examen Oftalmológico */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[#0B3B71] font-semibold border-b pb-2">
            <Eye className="h-5 w-5" />
            <span>Examen Oftalmológico</span>
          </div>
          
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.examen} onOpenChange={() => toggleSection('examen')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Examen Oftalmológico</span>
                    {openSections.examen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {/* Agudeza Visual */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Agudeza Visual</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div></div>
                      <div className="text-center text-slate-500">OD</div>
                      <div className="text-center text-slate-500">OS</div>
                      
                      <div className="text-slate-500 flex items-center">Sin Corrección</div>
                      <Input
                        placeholder="20/..."
                        value={currentEval.examen.agudeza_visual.od_sin_correccion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            agudeza_visual: { ...prev.examen.agudeza_visual, od_sin_correccion: e.target.value }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        placeholder="20/..."
                        value={currentEval.examen.agudeza_visual.os_sin_correccion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            agudeza_visual: { ...prev.examen.agudeza_visual, os_sin_correccion: e.target.value }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      
                      <div className="text-slate-500 flex items-center">Con Corrección</div>
                      <Input
                        placeholder="20/..."
                        value={currentEval.examen.agudeza_visual.od_con_correccion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            agudeza_visual: { ...prev.examen.agudeza_visual, od_con_correccion: e.target.value }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        placeholder="20/..."
                        value={currentEval.examen.agudeza_visual.os_con_correccion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            agudeza_visual: { ...prev.examen.agudeza_visual, os_con_correccion: e.target.value }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                    </div>
                  </div>

                  {/* Lensometría */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Lensometría</Label>
                    <div className="grid grid-cols-5 gap-2 mt-2 text-xs">
                      <div></div>
                      <div className="text-center text-slate-500">Esfera</div>
                      <div className="text-center text-slate-500">Cilindro</div>
                      <div className="text-center text-slate-500">Eje</div>
                      <div className="text-center text-slate-500">Adición</div>
                      
                      <div className="text-slate-500 flex items-center">OD</div>
                      <Input
                        placeholder=""
                        value={currentEval.examen.lensometria.od.esfera}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, od: { ...prev.examen.lensometria.od, esfera: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.lensometria.od.cilindro}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, od: { ...prev.examen.lensometria.od, cilindro: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.lensometria.od.eje}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, od: { ...prev.examen.lensometria.od, eje: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        data-testid="lensometria-od-adicion"
                        value={currentEval.examen.lensometria.od.adicion || ''}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, od: { ...prev.examen.lensometria.od, adicion: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      
                      <div className="text-slate-500 flex items-center">OS</div>
                      <Input
                        value={currentEval.examen.lensometria.os.esfera}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, os: { ...prev.examen.lensometria.os, esfera: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.lensometria.os.cilindro}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, os: { ...prev.examen.lensometria.os, cilindro: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.lensometria.os.eje}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, os: { ...prev.examen.lensometria.os, eje: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        data-testid="lensometria-os-adicion"
                        value={currentEval.examen.lensometria.os.adicion || ''}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            lensometria: { ...prev.examen.lensometria, os: { ...prev.examen.lensometria.os, adicion: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                    </div>
                  </div>

                  {/* Refracción */}
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-blue-600 font-medium">Refracción</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={copyRefraccionToReceta}
                        className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid="copy-refraccion-btn"
                      >
                        <Glasses className="h-3 w-3 mr-1" />
                        Copiar a receta
                      </Button>
                    </div>
                    <div className="grid grid-cols-5 gap-2 mt-2 text-xs">
                      <div></div>
                      <div className="text-center text-slate-500">Esfera</div>
                      <div className="text-center text-slate-500">Cilindro</div>
                      <div className="text-center text-slate-500">Eje</div>
                      <div className="text-center text-slate-500">Adición</div>

                      <div className="text-slate-500 flex items-center">OD</div>
                      <Input
                        data-testid="refraccion-od-esfera"
                        value={currentEval.examen.refraccion.od.esfera}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, od: { ...prev.examen.refraccion.od, esfera: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.od.cilindro}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, od: { ...prev.examen.refraccion.od, cilindro: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.od.eje}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, od: { ...prev.examen.refraccion.od, eje: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.od.adicion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, od: { ...prev.examen.refraccion.od, adicion: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />

                      <div className="text-slate-500 flex items-center">OS</div>
                      <Input
                        data-testid="refraccion-os-esfera"
                        value={currentEval.examen.refraccion.os.esfera}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, os: { ...prev.examen.refraccion.os, esfera: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.os.cilindro}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, os: { ...prev.examen.refraccion.os, cilindro: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.os.eje}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, os: { ...prev.examen.refraccion.os, eje: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                      <Input
                        value={currentEval.examen.refraccion.os.adicion}
                        onChange={(e) => setCurrentEval(prev => ({
                          ...prev,
                          examen: {
                            ...prev.examen,
                            refraccion: { ...prev.examen.refraccion, os: { ...prev.examen.refraccion.os, adicion: e.target.value } }
                          }
                        }))}
                        className="h-8 text-center"
                      />
                    </div>
                  </div>


                  {/* Presión Intraocular */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Presión Intraocular</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div>
                        <Label className="text-xs text-slate-500">OD (mmHg)</Label>
                        <Input
                          value={currentEval.examen.presion_intraocular.od}
                          onChange={(e) => setCurrentEval(prev => ({
                            ...prev,
                            examen: {
                              ...prev.examen,
                              presion_intraocular: { ...prev.examen.presion_intraocular, od: e.target.value }
                            }
                          }))}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">OS (mmHg)</Label>
                        <Input
                          value={currentEval.examen.presion_intraocular.os}
                          onChange={(e) => setCurrentEval(prev => ({
                            ...prev,
                            examen: {
                              ...prev.examen,
                              presion_intraocular: { ...prev.examen.presion_intraocular, os: e.target.value }
                            }
                          }))}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Método</Label>
                        <Input
                          placeholder="Goldmann..."
                          value={currentEval.examen.presion_intraocular.metodo}
                          onChange={(e) => setCurrentEval(prev => ({
                            ...prev,
                            examen: {
                              ...prev.examen,
                              presion_intraocular: { ...prev.examen.presion_intraocular, metodo: e.target.value }
                            }
                          }))}
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Segmento Anterior */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Segmento Anterior</Label>
                    <Textarea
                      placeholder="Párpados, conjuntiva, córnea, cámara anterior, iris, cristalino..."
                      value={currentEval.examen.segmento_anterior}
                      onChange={(e) => setCurrentEval(prev => ({
                        ...prev,
                        examen: { ...prev.examen, segmento_anterior: e.target.value }
                      }))}
                      className="min-h-[80px] resize-none mt-1"
                    />
                  </div>

                  {/* Segmento Posterior */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Segmento Posterior</Label>
                    <Textarea
                      placeholder="Vítreo, papila, mácula, retina, vasos..."
                      value={currentEval.examen.segmento_posterior}
                      onChange={(e) => setCurrentEval(prev => ({
                        ...prev,
                        examen: { ...prev.examen, segmento_posterior: e.target.value }
                      }))}
                      className="min-h-[80px] resize-none mt-1"
                    />
                  </div>

                  {/* Motilidad Ocular */}
                  <div>
                    <Label className="text-xs text-blue-600 font-medium">Motilidad Ocular</Label>
                    <Textarea
                      placeholder="Movimientos oculares, estrabismo..."
                      value={currentEval.examen.motilidad_ocular}
                      onChange={(e) => setCurrentEval(prev => ({
                        ...prev,
                        examen: { ...prev.examen, motilidad_ocular: e.target.value }
                      }))}
                      className="min-h-[60px] resize-none mt-1"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>

        {/* Column 3: Diagnóstico y Plan */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[#0B3B71] font-semibold border-b pb-2">
            <ClipboardList className="h-5 w-5" />
            <span>Diagnóstico y Plan</span>
          </div>
          
          {/* Diagnósticos */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.diagnostico} onOpenChange={() => toggleSection('diagnostico')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 text-blue-500" />
                      Diagnóstico y Plan
                      <Badge variant="secondary" className="ml-1">{currentEval.diagnostico_ids.length}</Badge>
                    </div>
                    {openSections.diagnostico ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  {/* Diagnósticos Selection with Autocomplete */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Diagnósticos</Label>
                    
                    {/* Search Input with Autocomplete */}
                    <div className="relative mb-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          ref={diagnosisInputRef}
                          placeholder="Escriba para buscar o crear diagnóstico..."
                          value={diagnosisSearch}
                          onChange={(e) => {
                            setDiagnosisSearch(e.target.value);
                            setShowDiagnosisDropdown(true);
                          }}
                          onFocus={() => setShowDiagnosisDropdown(true)}
                          onBlur={() => setTimeout(() => setShowDiagnosisDropdown(false), 200)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' || !diagnosisQuery) return;
                            e.preventDefault();
                            if (exactDiagnosis) {
                              if (!currentEval.diagnostico_ids.includes(exactDiagnosis.id)) addDiagnosis(exactDiagnosis.id);
                            } else if (filteredDiagnoses.length === 1) {
                              addDiagnosis(filteredDiagnoses[0].id);
                            } else {
                              handleQuickCreateDiagnosis();
                            }
                          }}
                          className="pl-9 h-9"
                          data-testid="diagnosis-search-input"
                        />
                      </div>
                      
                      {/* Autocomplete Dropdown */}
                      {showDiagnosisDropdown && diagnosisSearch.trim() && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-72 overflow-y-auto">
                          {!exactDiagnosisMatch && (
                            <button
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleQuickCreateDiagnosis}
                              disabled={creatingDiagnosis}
                              className="sticky top-0 z-10 w-full px-3 py-2.5 text-left bg-green-50 hover:bg-green-100 border-b flex items-center gap-2 text-green-700 transition-colors"
                              data-testid="create-new-diagnosis-button"
                            >
                              <Plus className="h-4 w-4" />
                              <span className="text-sm font-medium">
                                {creatingDiagnosis ? 'Creando...' : `Crear nuevo diagnóstico: "${diagnosisSearch.trim()}"`}
                              </span>
                            </button>
                          )}
                          {filteredDiagnoses.length > 10 && (
                            <p className="px-3 py-1.5 text-xs text-slate-400 bg-slate-50 border-b">
                              {filteredDiagnoses.length} coincidencias, mostrando 10. Siga escribiendo para afinar.
                            </p>
                          )}
                          {filteredDiagnoses.length > 0 ? (
                            <div>
                              {filteredDiagnoses.slice(0, 10).map(diag => (
                                <button
                                  key={diag.id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => addDiagnosis(diag.id)}
                                  className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center justify-between border-b last:border-b-0"
                                  data-testid={`diagnosis-option-${diag.id}`}
                                >
                                  <span className="text-sm font-medium text-slate-700">{diag.name}</span>
                                  {diag.category && (
                                    <span className="text-xs text-slate-400 ml-2">({diag.category})</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              {exactDiagnosisMatch ? 'Este diagnóstico ya está agregado' : 'No se encontraron diagnósticos'}
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                    
                    {/* Selected Diagnoses */}
                    <div className="flex flex-wrap gap-2">
                      {currentEval.diagnostico_ids.map(id => {
                        const diag = diagnoses.find(d => d.id === id);
                        return diag ? (
                          <Badge key={id} variant="secondary" className="flex items-center gap-1 py-1">
                            {diag.name}
                            <button onClick={() => removeDiagnosis(id)} className="ml-1 hover:text-red-500">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                      {currentEval.diagnostico_ids.length === 0 && (
                        <span className="text-xs text-slate-400 italic">Sin diagnósticos seleccionados</span>
                      )}
                    </div>
                  </div>

                  {/* Estudios a Realizar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-slate-500">Estudios a Realizar</Label>
                      <Button variant="ghost" size="sm" onClick={addEstudio} className="text-blue-600 h-7">
                        <Plus className="h-3 w-3 mr-1" /> Agregar
                      </Button>
                    </div>
                    {currentEval.estudios_a_realizar.map((est, idx) => (
                      <div key={est.id || idx} className="flex gap-2 mb-2">
                        <Input
                          placeholder="Nombre del estudio"
                          value={est.nombre}
                          onChange={(e) => {
                            const updated = [...currentEval.estudios_a_realizar];
                            updated[idx].nombre = e.target.value;
                            setCurrentEval(prev => ({ ...prev, estudios_a_realizar: updated }));
                          }}
                          className="flex-1"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeEstudio(idx)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Plan */}
                  <div>
                    <Label className="text-xs text-slate-500">Plan</Label>
                    <Textarea
                      placeholder="Conducta a seguir, indicaciones..."
                      value={currentEval.plan}
                      onChange={(e) => setCurrentEval(prev => ({ ...prev, plan: e.target.value }))}
                      className="min-h-[80px] resize-none mt-1"
                    />
                  </div>

                  {/* Lectura de Exámenes */}
                  <div>
                    <Label className="text-xs text-slate-500">Lectura de Exámenes</Label>
                    <Textarea
                      placeholder="Interpretación de estudios auxiliares, laboratorios, imágenes..."
                      value={currentEval.lectura_examenes}
                      onChange={(e) => setCurrentEval(prev => ({ ...prev, lectura_examenes: e.target.value }))}
                      className="min-h-[80px] resize-none mt-1"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Cirugía Recomendada */}
          <Card className={`border-0 shadow-sm ${currentEval.cirugia_recomendada.recomendar ? 'ring-2 ring-purple-400' : ''}`}>
            <Collapsible open={openSections.cirugiaRecomendada} onOpenChange={() => toggleSection('cirugiaRecomendada')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Scissors className="h-4 w-4 text-purple-500" />
                      Cirugía Recomendada
                      {currentEval.cirugia_recomendada.recomendar && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">Activo</Badge>
                      )}
                    </div>
                    {openSections.cirugiaRecomendada ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="recomendar-cirugia"
                      checked={currentEval.cirugia_recomendada.recomendar}
                      onCheckedChange={(checked) => setCurrentEval(prev => ({
                        ...prev,
                        cirugia_recomendada: { ...prev.cirugia_recomendada, recomendar: checked }
                      }))}
                    />
                    <label htmlFor="recomendar-cirugia" className="text-sm cursor-pointer">
                      Recomendar cirugía para este paciente
                    </label>
                  </div>
                  {currentEval.cirugia_recomendada.recomendar && (
                    <div className="space-y-3 pt-2 border-t">
                      <div>
                        <Label className="text-xs text-slate-500">Tipo de Cirugía *</Label>
                        <Select
                          value={currentEval.cirugia_recomendada.tipo_cirugia_id}
                          onValueChange={(value) => setCurrentEval(prev => ({
                            ...prev,
                            cirugia_recomendada: { ...prev.cirugia_recomendada, tipo_cirugia_id: value }
                          }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccione tipo de cirugía" />
                          </SelectTrigger>
                          <SelectContent>
                            {surgeryTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Nivel de Interés</Label>
                        <Select
                          value={currentEval.cirugia_recomendada.nivel_interes}
                          onValueChange={(value) => setCurrentEval(prev => ({
                            ...prev,
                            cirugia_recomendada: { ...prev.cirugia_recomendada, nivel_interes: value }
                          }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="alto">🔥 Alto</SelectItem>
                            <SelectItem value="medio">⚖️ Medio</SelectItem>
                            <SelectItem value="bajo">❄️ Bajo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Observaciones</Label>
                        <Textarea
                          placeholder="Notas sobre la recomendación quirúrgica..."
                          value={currentEval.cirugia_recomendada.observaciones}
                          onChange={(e) => setCurrentEval(prev => ({
                            ...prev,
                            cirugia_recomendada: { ...prev.cirugia_recomendada, observaciones: e.target.value }
                          }))}
                          className="min-h-[60px] resize-none mt-1"
                        />
                      </div>
                      <div className="p-2 bg-purple-50 rounded border border-purple-200 text-sm text-purple-700">
                        <Scissors className="h-4 w-4 inline mr-1" />
                        Al guardar, este paciente se agregará automáticamente al CRM de Cirugías.
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Próxima Cita */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.proximaCita} onOpenChange={() => toggleSection('proximaCita')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      Próxima Cita
                    </div>
                    {openSections.proximaCita ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3" data-testid="proxima-cita-section">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="agendar-cita"
                      data-testid="agendar-cita-checkbox"
                      checked={currentEval.proxima_cita.agendar}
                      onCheckedChange={(checked) => setCurrentEval(prev => ({
                        ...prev,
                        proxima_cita: { ...prev.proxima_cita, agendar: checked }
                      }))}
                    />
                    <label htmlFor="agendar-cita" className="text-sm cursor-pointer">
                      Agendar próxima cita para este paciente
                    </label>
                  </div>
                  {currentEval.proxima_cita.agendar && (
                    <div className="space-y-3" data-testid="proxima-cita-form">
                      <div>
                        <Label className="text-xs text-slate-500">Fecha</Label>
                        <Input
                          type="date"
                          data-testid="proxima-cita-fecha"
                          value={currentEval.proxima_cita.fecha}
                          min={format(new Date(), 'yyyy-MM-dd')}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            setCurrentEval(prev => ({
                              ...prev,
                              proxima_cita: { ...prev.proxima_cita, fecha: selectedDate, hora: '' }
                            }));
                            // Check availability and fetch slots
                            if (selectedDate) {
                              if (!isDateAvailable(selectedDate)) {
                                toast.error('Esta fecha no está disponible para citas');
                              }
                            }
                          }}
                          className="h-8"
                        />
                        {currentEval.proxima_cita.fecha && !isDateAvailable(currentEval.proxima_cita.fecha) && (
                          <p className="text-xs text-red-500 mt-1">Esta fecha no está disponible</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Hora disponible</Label>
                        <AvailableTimeSelect
                          date={currentEval.proxima_cita.fecha}
                          value={currentEval.proxima_cita.hora}
                          onChange={(value) => setCurrentEval(prev => ({
                            ...prev,
                            proxima_cita: { ...prev.proxima_cita, hora: value }
                          }))}
                          testId="proxima-cita-hora"
                          className="h-8"
                        />
                      </div>
                      {currentEval.proxima_cita.fecha && currentEval.proxima_cita.hora && (
                        <div className="p-2 bg-green-50 rounded border border-green-200" data-testid="proxima-cita-confirmation">
                          <p className="text-sm text-green-700">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            Cita: {format(new Date(currentEval.proxima_cita.fecha + 'T12:00:00'), "EEEE d 'de' MMMM, yyyy", { locale: es })} a las {currentEval.proxima_cita.hora}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Receta de Medicamentos */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.recetaMedicamentos} onOpenChange={() => toggleSection('recetaMedicamentos')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Pill className="h-4 w-4 text-blue-500" />
                      Receta de Medicamentos
                      <Badge variant="secondary" className="ml-1">{currentEval.receta_medicamentos.length}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleWhatsApp('medicamentos'); }} className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" title="Enviar por WhatsApp">
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handlePrint('medicamentos'); }} className="h-6 w-6 p-0" title="Imprimir">
                        <Printer className="h-4 w-4" />
                      </Button>
                      {openSections.recetaMedicamentos ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500">Medicamentos</Label>
                    <Button variant="ghost" size="sm" onClick={() => addMedicamento('receta')} className="text-blue-600 h-7">
                      <Plus className="h-3 w-3 mr-1" /> Agregar
                    </Button>
                  </div>
                  {currentEval.receta_medicamentos.map((med, idx) => (
                    <div key={med.id || idx} className="p-3 bg-slate-50 rounded-lg space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 shrink-0">
                            {idx + 1}
                          </Badge>
                          <Popover open={openMedPopover === idx} onOpenChange={(open) => setOpenMedPopover(open ? idx : null)}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full justify-between font-normal h-9"
                                onClick={() => {
                                  if (openMedPopover !== idx) {
                                    fetchMedications(med.nombre || '');
                                  }
                                }}
                              >
                                {med.nombre || "Buscar medicamento..."}
                                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0" align="start">
                              <Command>
                                <CommandInput 
                                  placeholder="Buscar o agregar medicamento..." 
                                  value={medicationSearch}
                                  onValueChange={(value) => {
                                    setMedicationSearch(value);
                                    if (value.length >= 2) {
                                      fetchMedications(value);
                                    }
                                  }}
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    {medicationSearch.length >= 2 ? (
                                      <div className="p-2">
                                        <p className="text-sm text-slate-500 mb-2">No encontrado en el catálogo</p>
                                        <Button
                                          size="sm"
                                          className="w-full bg-blue-600 hover:bg-blue-700"
                                          onClick={async () => {
                                            const newMed = await createMedication(medicationSearch);
                                            if (newMed) {
                                              const updated = [...currentEval.receta_medicamentos];
                                              updated[idx].nombre = medicationSearch;
                                              setCurrentEval(prev => ({ ...prev, receta_medicamentos: updated }));
                                              setOpenMedPopover(null);
                                              setMedicationSearch('');
                                            }
                                          }}
                                        >
                                          <Plus className="h-4 w-4 mr-1" />
                                          Crear "{medicationSearch}"
                                        </Button>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-slate-500 p-2">Escribe al menos 2 caracteres</p>
                                    )}
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {medicationsCatalog.map((medication) => (
                                      <CommandItem
                                        key={medication.id}
                                        value={medication.nombre}
                                        onSelect={() => {
                                          const updated = [...currentEval.receta_medicamentos];
                                          updated[idx].nombre = medication.nombre;
                                          setCurrentEval(prev => ({ ...prev, receta_medicamentos: updated }));
                                          setOpenMedPopover(null);
                                          setMedicationSearch('');
                                          // Increment usage count
                                          axios.post(`${API}/medications/increment-usage/${medication.id}`, {}, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          }).catch(() => {});
                                        }}
                                      >
                                        <div className="flex flex-col">
                                          <span>{medication.nombre}</span>
                                          {medication.presentacion && (
                                            <span className="text-xs text-slate-400">{medication.presentacion}</span>
                                          )}
                                        </div>
                                        {med.nombre === medication.nombre && (
                                          <Check className="ml-auto h-4 w-4 text-blue-600" />
                                        )}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                  
                                  {/* Always show "Create new" option when there's a search term */}
                                  {medicationSearch.length >= 2 && (
                                    <div className="p-2 border-t">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                                        onClick={async () => {
                                          const newMed = await createMedication(medicationSearch);
                                          if (newMed) {
                                            const updated = [...currentEval.receta_medicamentos];
                                            updated[idx].nombre = medicationSearch;
                                            setCurrentEval(prev => ({ ...prev, receta_medicamentos: updated }));
                                            setOpenMedPopover(null);
                                            setMedicationSearch('');
                                          }
                                        }}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Crear nuevo: "{medicationSearch}"
                                      </Button>
                                    </div>
                                  )}
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeMedicamento('receta', idx)} className="text-red-500 shrink-0">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Indicaciones: dosis, frecuencia, duración, vía de administración, instrucciones especiales..."
                        value={med.instrucciones}
                        onChange={(e) => {
                          const updated = [...currentEval.receta_medicamentos];
                          updated[idx].instrucciones = e.target.value;
                          setCurrentEval(prev => ({ ...prev, receta_medicamentos: updated }));
                        }}
                        className="min-h-[80px] resize-none"
                      />
                    </div>
                  ))}

                  {/* Observaciones de la receta (texto plano) */}
                  <div className="space-y-1.5 pt-1">
                    <Label className="text-xs text-slate-500">Observaciones</Label>
                    <Textarea
                      data-testid="receta-medicamentos-observaciones"
                      placeholder="Observaciones adicionales para la receta..."
                      value={currentEval.receta_medicamentos_observaciones || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCurrentEval(prev => ({ ...prev, receta_medicamentos_observaciones: value }));
                      }}
                      className="min-h-[70px] resize-none"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Recetas de Anteojos - Multiple */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.recetaAnteojos} onOpenChange={() => toggleSection('recetaAnteojos')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Glasses className="h-4 w-4 text-blue-500" />
                      Recetas de Anteojos
                      <Badge variant="secondary" className="text-xs" data-testid="recetas-anteojos-count">{currentEval.recetas_anteojos?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); addRecetaAnteojos(); }} className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Agregar receta" data-testid="add-receta-anteojos-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        <span className="text-xs">Agregar</span>
                      </Button>
                      {openSections.recetaAnteojos ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {currentEval.recetas_anteojos?.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No hay recetas de anteojos. Haz clic en "Agregar" para crear una.</p>
                  ) : (
                    currentEval.recetas_anteojos?.map((receta, idx) => (
                      <div key={receta.id} className="border rounded-lg p-3 space-y-3 bg-slate-50" data-testid={`receta-anteojos-${idx}`}>
                        <div className="flex items-center justify-between">
                          <Input
                            value={receta.nombre || ''}
                            onChange={(e) => updateRecetaAnteojos(receta.id, 'nombre', e.target.value)}
                            placeholder={`Receta ${idx + 1} (ej: Lejos, Cerca, Bifocal)`}
                            className="h-7 text-sm font-medium w-48"
                            data-testid={`receta-anteojos-nombre-${idx}`}
                          />
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => sendViaWhatsApp('anteojos', receta.id)} className="h-6 w-6 p-0 text-green-600" title="WhatsApp" data-testid={`receta-anteojos-whatsapp-${idx}`}>
                              <MessageCircle className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handlePrint('anteojos', receta.id)} className="h-6 w-6 p-0" title="Imprimir" data-testid={`receta-anteojos-print-${idx}`}>
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => removeRecetaAnteojos(receta.id)} className="h-6 w-6 p-0 text-red-500" title="Eliminar" data-testid={`receta-anteojos-delete-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-6 gap-2 text-xs">
                          <div></div>
                          <div className="text-center text-slate-500">Esfera</div>
                          <div className="text-center text-slate-500">Cilindro</div>
                          <div className="text-center text-slate-500">Eje</div>
                          <div className="text-center text-slate-500">Adición</div>
                          <div className="text-center text-slate-500">DP</div>
                          
                          <div className="text-slate-500 flex items-center">OD</div>
                          <Input value={receta.od_esfera || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'od_esfera', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_cilindro || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'od_cilindro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_eje || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'od_eje', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_adicion || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'od_adicion', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_dp || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'od_dp', e.target.value)} className="h-7 text-center text-xs" />
                          
                          <div className="text-slate-500 flex items-center">OS</div>
                          <Input value={receta.os_esfera || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'os_esfera', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_cilindro || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'os_cilindro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_eje || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'os_eje', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_adicion || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'os_adicion', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_dp || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'os_dp', e.target.value)} className="h-7 text-center text-xs" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Select value={receta.tipo_lente || ''} onValueChange={(v) => updateRecetaAnteojos(receta.id, 'tipo_lente', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Tipo lente" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monofocal">Monofocal</SelectItem>
                              <SelectItem value="bifocal">Bifocal</SelectItem>
                              <SelectItem value="progresivo">Progresivo</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input value={receta.material || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'material', e.target.value)} placeholder="Material" className="h-7 text-xs" />
                          <Input value={receta.tratamientos || ''} onChange={(e) => updateRecetaAnteojos(receta.id, 'tratamientos', e.target.value)} placeholder="Tratamientos" className="h-7 text-xs" />
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Recetas de Lentes de Contacto - Multiple */}
          <Card className="border-0 shadow-sm">
            <Collapsible open={openSections.recetaLentesContacto} onOpenChange={() => toggleSection('recetaLentesContacto')}>
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 rounded-t-lg">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Contact className="h-4 w-4 text-blue-500" />
                      Recetas de Lentes de Contacto
                      <Badge variant="secondary" className="text-xs" data-testid="recetas-lentes-contacto-count">{currentEval.recetas_lentes_contacto?.length || 0}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); addRecetaLentesContacto(); }} className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Agregar receta" data-testid="add-receta-lentes-contacto-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        <span className="text-xs">Agregar</span>
                      </Button>
                      {openSections.recetaLentesContacto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {currentEval.recetas_lentes_contacto?.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No hay recetas de lentes de contacto. Haz clic en "Agregar" para crear una.</p>
                  ) : (
                    currentEval.recetas_lentes_contacto?.map((receta, idx) => (
                      <div key={receta.id} className="border rounded-lg p-3 space-y-3 bg-slate-50" data-testid={`receta-lentes-contacto-${idx}`}>
                        <div className="flex items-center justify-between">
                          <Input
                            value={receta.nombre || ''}
                            onChange={(e) => updateRecetaLentesContacto(receta.id, 'nombre', e.target.value)}
                            placeholder={`Receta ${idx + 1} (ej: Diario, Mensual)`}
                            className="h-7 text-sm font-medium w-48"
                            data-testid={`receta-lentes-contacto-nombre-${idx}`}
                          />
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => sendViaWhatsApp('lentes_contacto', receta.id)} className="h-6 w-6 p-0 text-green-600" title="WhatsApp" data-testid={`receta-lentes-contacto-whatsapp-${idx}`}>
                              <MessageCircle className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handlePrint('lentes_contacto', receta.id)} className="h-6 w-6 p-0" title="Imprimir" data-testid={`receta-lentes-contacto-print-${idx}`}>
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => removeRecetaLentesContacto(receta.id)} className="h-6 w-6 p-0 text-red-500" title="Eliminar" data-testid={`receta-lentes-contacto-delete-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-2 text-xs">
                          <div></div>
                          <div className="text-center text-slate-500">Esfera</div>
                          <div className="text-center text-slate-500">Cilindro</div>
                          <div className="text-center text-slate-500">Eje</div>
                          <div className="text-center text-slate-500">CB</div>
                          <div className="text-center text-slate-500">DIA</div>
                          <div className="text-center text-slate-500">Marca</div>
                          
                          <div className="text-slate-500 flex items-center">OD</div>
                          <Input value={receta.od_esfera || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_esfera', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_cilindro || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_cilindro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_eje || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_eje', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_curva_base || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_curva_base', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_diametro || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_diametro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.od_marca || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'od_marca', e.target.value)} className="h-7 text-center text-xs" />
                          
                          <div className="text-slate-500 flex items-center">OS</div>
                          <Input value={receta.os_esfera || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_esfera', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_cilindro || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_cilindro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_eje || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_eje', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_curva_base || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_curva_base', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_diametro || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_diametro', e.target.value)} className="h-7 text-center text-xs" />
                          <Input value={receta.os_marca || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'os_marca', e.target.value)} className="h-7 text-center text-xs" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Select value={receta.tipo || ''} onValueChange={(v) => updateRecetaLentesContacto(receta.id, 'tipo', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Tipo" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="blandos">Blandos</SelectItem>
                              <SelectItem value="rgp">RGP</SelectItem>
                              <SelectItem value="hibridos">Híbridos</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={receta.uso || ''} onValueChange={(v) => updateRecetaLentesContacto(receta.id, 'uso', v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Uso" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="diario">Diario</SelectItem>
                              <SelectItem value="quincenal">Quincenal</SelectItem>
                              <SelectItem value="mensual">Mensual</SelectItem>
                              <SelectItem value="anual">Anual</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input value={receta.observaciones || ''} onChange={(e) => updateRecetaLentesContacto(receta.id, 'observaciones', e.target.value)} placeholder="Observaciones" className="h-7 text-xs" />
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </div>
      </div>

      {/* Historial Dialog */}
      <Dialog open={showHistorial} onOpenChange={setShowHistorial}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historial de Evaluaciones - {patient?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {evaluations.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay evaluaciones previas</p>
            ) : (
              evaluations.map(evaluation => (
                <Card key={evaluation.id} className="border shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{format(new Date(evaluation.fecha + 'T12:00:00'), "EEEE d 'de' MMMM, yyyy", { locale: es })}</p>
                        <p className="text-sm text-slate-500 mt-1">{evaluation.motivo_consulta}</p>
                        {evaluation.diagnosticos?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {evaluation.diagnosticos.map(d => (
                              <Badge key={d.id} variant="outline" className="text-xs">{d.name}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleLoadEvaluation(evaluation)}>
                          Cargar
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteEvaluation(evaluation.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistorial(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vista Previa de Impresión</DialogTitle>
            <p className="text-xs text-slate-500">Tamaño: Media carta horizontal (5.5" x 8.5")</p>
          </DialogHeader>
          
          {/* Printable Content - Half Letter Landscape - Simplified */}
          <div 
            className="print-prescription bg-white border-2 border-slate-200 rounded-lg overflow-hidden relative" 
            id="print-content"
            style={{ 
              width: '100%',
              minHeight: '380px',
              aspectRatio: '8.5 / 5.5'
            }}
          >
            {/* Content Container - 1.5cm top margin for print */}
            <div className="relative z-10 px-6 pb-6" style={{ paddingTop: '1.5cm' }}>
              {/* Patient Info */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm border-b border-slate-200 pb-4">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Paciente:</span>
                  <span className="font-semibold text-slate-800">{patient?.full_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Edad:</span>
                  <span className="font-semibold text-slate-800">
                    {patient?.birth_date ? `${calculateAge(patient.birth_date)} años` : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Fecha:</span>
                  <span className="font-semibold text-slate-800">{format(new Date(), "d 'de' MMMM, yyyy", { locale: es })}</span>
                </div>
                {currentEval.proxima_cita?.agendar && currentEval.proxima_cita?.fecha && currentEval.proxima_cita?.hora && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Próxima Cita:</span>
                    <span className="font-semibold text-[#0891B2]">
                      {format(new Date(currentEval.proxima_cita.fecha + 'T12:00:00'), "d/MM/yyyy", { locale: es })} - {currentEval.proxima_cita.hora}
                    </span>
                  </div>
                )}
              </div>

              {/* Prescription Content - Medicamentos */}
              {printType === 'medicamentos' && (
                <div>
                  <h3 className="font-bold text-slate-800 border-b-2 border-slate-300 mb-4 pb-2 text-sm uppercase tracking-wide">
                    Medicamentos Recetados
                  </h3>
                  
                  {currentEval.receta_medicamentos.length > 0 ? (
                    <div className="space-y-4">
                      {currentEval.receta_medicamentos.slice(0, 5).map((med, idx) => (
                        <div key={med.id || idx} className="border-l-2 border-slate-300 pl-3">
                          <div className="flex items-start gap-2">
                            <span className="font-bold text-slate-600 text-sm">{idx + 1}.</span>
                            <div className="flex-1">
                              <p className="font-bold text-slate-800 text-sm">{med.nombre || 'Sin nombre'}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-1">
                                {med.dosis && (
                                  <span><span className="text-slate-400">Dosis:</span> {med.dosis}</span>
                                )}
                                {med.frecuencia && (
                                  <span><span className="text-slate-400">Frecuencia:</span> {med.frecuencia}</span>
                                )}
                                {med.duracion && (
                                  <span><span className="text-slate-400">Duración:</span> {med.duracion}</span>
                                )}
                                {med.via && (
                                  <span><span className="text-slate-400">Vía:</span> {med.via}</span>
                                )}
                              </div>
                              {med.instrucciones && (
                                <p className="text-xs text-slate-700 mt-1.5 flex items-start gap-1.5">
                                  <span className="text-slate-400 font-bold">➤</span>
                                  <span className="italic">{med.instrucciones}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {currentEval.receta_medicamentos.length > 5 && (
                        <p className="text-xs text-amber-600 italic mt-3 border-t border-amber-200 pt-2">
                          ⚠ Se muestran los primeros 5 medicamentos. Esta receta contiene {currentEval.receta_medicamentos.length} medicamentos en total.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm italic">No hay medicamentos en esta receta</p>
                  )}

                  {currentEval.receta_medicamentos_observaciones && (
                    <div className="mt-4 pt-3 border-t border-slate-200">
                      <p className="font-bold text-slate-600 text-xs uppercase tracking-wide mb-1">Observaciones</p>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap">{currentEval.receta_medicamentos_observaciones}</p>
                    </div>
                  )}
                </div>
              )}

              {printType === 'anteojos' && printRecetaId && (() => {
                const receta = currentEval.recetas_anteojos?.find(r => r.id === printRecetaId);
                if (!receta) return <p className="text-slate-400">Receta no encontrada</p>;
                return (
                  <div>
                    <h3 className="font-bold text-slate-800 border-b-2 border-slate-300 mb-4 pb-2 text-sm uppercase tracking-wide">
                      Receta de Anteojos{receta.nombre ? ` - ${receta.nombre}` : ''}
                    </h3>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="border border-slate-300 p-2 w-12"></th>
                          <th className="border border-slate-300 p-2 text-slate-700">Esfera</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Cilindro</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Eje</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Adición</th>
                          <th className="border border-slate-300 p-2 text-slate-700">DP</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 p-2 font-bold text-center bg-slate-50 text-slate-700">OD</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_esfera || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_cilindro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_eje || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_adicion || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_dp || '-'}</td>
                        </tr>
                        <tr>
                          <td className="border border-slate-300 p-2 font-bold text-center bg-slate-50 text-slate-700">OS</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_esfera || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_cilindro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_eje || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_adicion || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_dp || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-3 text-sm space-y-1">
                      {receta.tipo_lente && (
                        <p><span className="text-slate-500">Tipo de lente:</span> <span className="font-medium">{receta.tipo_lente}</span></p>
                      )}
                      {receta.material && (
                        <p><span className="text-slate-500">Material:</span> <span className="font-medium">{receta.material}</span></p>
                      )}
                      {receta.tratamientos && (
                        <p><span className="text-slate-500">Tratamientos:</span> <span className="font-medium">{receta.tratamientos}</span></p>
                      )}
                      {receta.observaciones && (
                        <p><span className="text-slate-500">Observaciones:</span> <span className="font-medium">{receta.observaciones}</span></p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {printType === 'lentes_contacto' && printRecetaId && (() => {
                const receta = currentEval.recetas_lentes_contacto?.find(r => r.id === printRecetaId);
                if (!receta) return <p className="text-slate-400">Receta no encontrada</p>;
                return (
                  <div>
                    <h3 className="font-bold text-slate-800 border-b-2 border-slate-300 mb-4 pb-2 text-sm uppercase tracking-wide">
                      Receta de Lentes de Contacto{receta.nombre ? ` - ${receta.nombre}` : ''}
                    </h3>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="border border-slate-300 p-2 w-12"></th>
                          <th className="border border-slate-300 p-2 text-slate-700">Esfera</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Cilindro</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Eje</th>
                          <th className="border border-slate-300 p-2 text-slate-700">CB</th>
                          <th className="border border-slate-300 p-2 text-slate-700">DIA</th>
                          <th className="border border-slate-300 p-2 text-slate-700">Marca</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 p-2 font-bold text-center bg-slate-50 text-slate-700">OD</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_esfera || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_cilindro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_eje || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_curva_base || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_diametro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.od_marca || '-'}</td>
                        </tr>
                        <tr>
                          <td className="border border-slate-300 p-2 font-bold text-center bg-slate-50 text-slate-700">OS</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_esfera || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_cilindro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_eje || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_curva_base || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_diametro || '-'}</td>
                          <td className="border border-slate-300 p-2 text-center">{receta.os_marca || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-3 text-sm space-y-1">
                      {receta.tipo && (
                        <p><span className="text-slate-500">Tipo:</span> <span className="font-medium">{receta.tipo}</span></p>
                      )}
                      {receta.uso && (
                        <p><span className="text-slate-500">Uso:</span> <span className="font-medium">{receta.uso}</span></p>
                      )}
                      {receta.observaciones && (
                        <p><span className="text-slate-500">Observaciones:</span> <span className="font-medium">{receta.observaciones}</span></p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cancelar</Button>
            <Button onClick={executePrint} className="bg-[#0891B2] hover:bg-[#0e7490]">
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Diagnosis Dialog */}
      <Dialog open={showNewDiagnosisDialog} onOpenChange={setShowNewDiagnosisDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B3B71] flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nuevo Diagnóstico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Diagnóstico *</Label>
              <Input
                value={newDiagnosis.name}
                onChange={(e) => setNewDiagnosis(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Queratocono"
                data-testid="new-diagnosis-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría (opcional)</Label>
              <Input
                value={newDiagnosis.category}
                onChange={(e) => setNewDiagnosis(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Ej: Córnea, Retina, Glaucoma..."
                data-testid="new-diagnosis-category"
              />
              <p className="text-xs text-slate-400">
                Categorías existentes: Refractivo, Retina, Glaucoma, Superficie ocular, Cristalino, etc.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNewDiagnosisDialog(false);
                setNewDiagnosis({ name: '', category: '' });
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateDiagnosis}
              disabled={creatingDiagnosis || !newDiagnosis.name.trim()}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-diagnosis-button"
            >
              {creatingDiagnosis ? 'Guardando...' : 'Crear y Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvaluacionKanbanPage;
